import { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "../api";
import { useApp } from "../context/AppContext";
import { MODULES, ORDER_STATUS, clientName, fmtCHF, fmtDateTime, orderSummary } from "../utils";
import ArticleAdder, { ArticleRows } from "../components/ArticleAdder";
import { toast } from "sonner";
import { CheckCircle2, Clock, Plus } from "lucide-react";

export default function MyDay() {
  const { user, module } = useApp();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ started_at: "", ended_at: "", materials: [], receipts: [], notes: "", articles: [] });
  const [prices, setPrices] = useState([]);
  const [mat, setMat] = useState({ name: "", price: "" });

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  const load = () => {
    const params = {};
    if (module !== "tous") params.module = module;
    api.get("/orders", { params }).then((r) => setOrders(r.data));
  };
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); api.get("/price-list").then((r) => setPrices(r.data)); }, [module]);

  const today = new Date().toISOString().slice(0, 10);
  const todays = orders.filter((o) => (o.scheduled_date || "").startsWith(today));
  const upcoming = orders.filter((o) => !(o.scheduled_date || "").startsWith(today) && o.status !== "facture");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/orders/${target.id}/realize`, {
        started_at: form.started_at ? new Date(form.started_at).toISOString() : null,
        ended_at: form.ended_at ? new Date(form.ended_at).toISOString() : null,
        materials: form.materials, receipts: form.receipts, notes: form.notes || null,
        details: { ...target.details, articles: form.articles },
      });
      toast.success(`Bon ${target.order_number} réalisé — merci !`);
      setTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const uploadReceipts = async (e) => {
    for (const f of e.target.files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((prev) => ({ ...prev, receipts: [...prev.receipts, { name: r.data.name, file_id: r.data.id }] }));
        toast.success(`Reçu « ${r.data.name} » téléversé`);
      } catch (err) { toast.error(errMsg(err)); }
    }
    e.target.value = "";
  };

  const OrderCard = ({ o }) => {
    const M = MODULES[o.module];
    const st = ORDER_STATUS[o.status];
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-2" data-testid={`myday-order-${o.id}`}>
        <div className="flex items-center justify-between">
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${M.badge}`}>{M.label}</span>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
        </div>
        <div className="font-semibold text-slate-900">{clientName(clientMap[o.client_id] || {})}</div>
        <div className="text-sm text-slate-600">{orderSummary(o)}{o.description ? ` — ${o.description}` : ""}</div>
        <div className="text-xs text-slate-400 font-mono2">{o.order_number}</div>
        {o.status !== "realise" && o.status !== "facture" && (
          <button data-testid={`clock-in-button-${o.id}`}
            onClick={() => { setForm({ started_at: "", ended_at: "", materials: [], receipts: [], notes: "", articles: o.details?.articles || [] }); setTarget(o); }}
            className="w-full mt-1 flex items-center justify-center gap-2 bg-slate-900 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors">
            <Clock size={15} /> Pointer & terminer
          </button>
        )}
        {(o.status === "realise" || o.status === "facture") && (
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <CheckCircle2 size={14} /> Réalisé {o.started_at ? `· ${fmtDateTime(o.started_at)} → ${fmtDateTime(o.ended_at)}` : ""}
            {o.materials?.length > 0 && <span>· Matériel {fmtCHF(o.materials.reduce((a, m) => a + m.price, 0))}</span>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 max-w-lg mx-auto space-y-5 pb-24" data-testid="mobile-field-portal">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Bonjour {user.name.split(" ")[0]}</h1>
        <p className="text-sm text-slate-500">Vos interventions {module !== "tous" ? `· ${MODULES[module]?.label}` : ""}</p>
      </div>

      <section className="space-y-3">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-testid="myday-today-section">Aujourd'hui ({todays.length})</h2>
        {todays.length === 0 && <div className="text-sm text-slate-400 bg-white border border-slate-200 rounded-xl p-4">Aucune intervention prévue aujourd'hui.</div>}
        {todays.map((o) => <OrderCard key={o.id} o={o} />)}
      </section>

      <section className="space-y-3">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-testid="myday-upcoming-section">À venir / en retard ({upcoming.length})</h2>
        {upcoming.map((o) => <OrderCard key={o.id} o={o} />)}
      </section>

      {target && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-end sm:items-center justify-center z-50" data-testid="realize-order-modal">
          <form onSubmit={submit} className="bg-white rounded-t-2xl sm:rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-slate-900">Terminer {target.order_number}</h2>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Heure d'arrivée (facultatif)</label>
              <input data-testid="realize-started" type="datetime-local" value={form.started_at} onChange={(e) => setForm({ ...form, started_at: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-3 text-base" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Heure de départ (facultatif)</label>
              <input data-testid="realize-ended" type="datetime-local" value={form.ended_at} onChange={(e) => setForm({ ...form, ended_at: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-3 text-base" />
            </div>
            <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 space-y-2" data-testid="realize-articles">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Prestations {MODULES[target.module].label} — quantités réelles (prix verrouillés)</label>
              <ArticleRows articles={form.articles} setArticles={(arts) => setForm({ ...form, articles: arts })} prefix="realize" />
              <ArticleAdder articles={form.articles} setArticles={(arts) => setForm({ ...form, articles: arts })} prices={prices.filter((p) => p.module === target.module)} prefix="realize" />
              <p className="text-[11px] text-slate-400">Les prix viennent de la liste officielle du module et ne sont pas modifiables.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Matériel acheté (montants TTC)</label>
              <div className="flex gap-2 mt-1">
                <input data-testid="realize-material-name" placeholder="Produit" value={mat.name} onChange={(e) => setMat({ ...mat, name: e.target.value })}
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm" />
                <input data-testid="realize-material-price" placeholder="CHF TTC" type="number" step="0.05" value={mat.price} onChange={(e) => setMat({ ...mat, price: e.target.value })}
                  className="w-24 rounded-lg border border-slate-300 px-3 py-2.5 text-sm" />
                <button type="button" data-testid="realize-material-add"
                  onClick={() => { if (mat.name && mat.price) { setForm({ ...form, materials: [...form.materials, { name: mat.name, price: parseFloat(mat.price) }] }); setMat({ name: "", price: "" }); } }}
                  className="px-3 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"><Plus size={16} /></button>
              </div>
              <div className="mt-2 space-y-1" data-testid="realize-materials-list">
                {form.materials.map((m, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm">
                    <span>{m.name}</span><span className="font-semibold">{fmtCHF(m.price)} TTC</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Reçus (photos / PDF)</label>
              <input data-testid="realize-receipts" type="file" multiple accept="image/*,application/pdf" onChange={uploadReceipts}
                className="mt-1 w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white file:px-3 file:py-1.5 file:text-xs file:font-semibold" />
              <div className="mt-2 space-y-1" data-testid="realize-receipts-list">
                {form.receipts.map((r, i) => (
                  <div key={i} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-700">{r.name}</div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes</label>
              <input data-testid="realize-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm" />
            </div>
            <div className="flex gap-2 pt-1">
              <button type="button" data-testid="realize-cancel" onClick={() => setTarget(null)}
                className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200">Annuler</button>
              <button type="submit" data-testid="realize-submit"
                className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700">Confirmer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
